import {type UseFormReturnType, useForm, zodResolver} from '@mantine/form';
import {Notifications} from '@mantine/notifications';
import Head from 'next/head';
import {useRouter} from 'next/router';
import type {FC} from 'react';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {TITLE_SUFFIX} from '../../../config/constants/meta';
import {LoginSchema} from '../../../core/entities/login/schema';
import type {ILogin} from '../../../core/repositories/loginRepository';
import {SectionTitle} from '../../../shared/components/SectionTitle';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {useHandleLogin} from '../usecase/useHandleLogin';
import {LoginPageForMobile} from './Container/LoginPageForMobile';
import {LoginPageForPc} from './Container/LoginPageForPc';

export type UseFormLoginReturnType = UseFormReturnType<
  ILogin,
  (values: ILogin) => ILogin
>;

type Props = {
  nextRedirectUrl: string | undefined;
};

export const LoginPage: FC<Props> = ({nextRedirectUrl}) => {
  const router = useRouter();
  const {isPC} = useMediaQuery();

  const currentUrl = `${GSSKT_HOST}${decodeURI(router.pathname)}`;
  const TITLE = 'ログイン';

  const useFormLogin = useForm({
    validate: zodResolver(LoginSchema),
    initialValues: {
      email: '',
      password: '',
    },
  });

  const {submitLogin, isLoading, hasError} = useHandleLogin({
    email: useFormLogin.values.email,
    password: useFormLogin.values.password,
    nextRedirectUrl,
  });

  return (
    <>
      <Head>
        <title key="title">{`${TITLE}${TITLE_SUFFIX}`}</title>
        <meta
          key="og:title"
          property="og:title"
          content={`${TITLE}${TITLE_SUFFIX}`}
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          name="keywords"
          content="外資,就活,インターンシップ,新卒採用,会社説明会,コラム,ES・体験記,投資銀行,コンサル,商社"
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta
          name="description"
          content="外資就活ドットコムのログインページです。トップ企業の新卒採用情報から、インターンシップ・先輩のエントリーシート・ES・体験記・コラム・TOEIC対策まで就職活動に役に立つ多様なコンテンツを掲載。"
        />
      </Head>
      <Notifications position={'top-right'} />
      {/* for SEO */}
      <SectionTitle order={1} title={TITLE} className="hidden" />

      {isPC !== undefined &&
        (isPC ? (
          <LoginPageForPc
            useFormLogin={useFormLogin}
            submitLogin={submitLogin}
            isLoading={isLoading}
            hasError={hasError}
          />
        ) : (
          <LoginPageForMobile
            useFormLogin={useFormLogin}
            submitLogin={submitLogin}
            isLoading={isLoading}
            hasError={hasError}
          />
        ))}
    </>
  );
};
